import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_URL, formatDate, getStatusBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  FileText,
  Loader2,
  Building2,
  UserCheck,
  TrendingUp,
  DollarSign,
  Settings,
  CreditCard,
  Shield,
  Upload,
  Rss,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Mail,
  MessageSquare,
  MousePointerClick,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  Clock,
  BadgeCheck,
  ExternalLink,
  XCircle,
  MapPin,
  Bell,
  Eye,
} from 'lucide-react';
import { useAdminCtx } from './AdminContext';


const AdminTabJobs = () => {
  const { jobs } = useAdminCtx();
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [onlyMissingSalary, setOnlyMissingSalary] = useState(false);

  const isMissingSalary = (j) => !j.is_external && (j.salary_min == null || j.salary_max == null);
  const missingCount = (jobs || []).filter(isMissingSalary).length;
  const visibleJobs = onlyMissingSalary ? (jobs || []).filter(isMissingSalary) : (jobs || []);

  const runCleanup = async () => {
    if (!window.confirm('Re-apply the strict Social-Work-Required rule to all jobs? Imported jobs that don\'t qualify will be hidden (reversible). Employer-posted jobs are only flagged, never removed.')) return;
    setCleaning(true);
    setCleanupResult(null);
    try {
      const res = await axios.post(`${API_URL}/admin/jobs/cleanup-non-sw`, {});
      setCleanupResult(res.data);
      toast.success(`Cleanup complete — ${res.data.active_after} social-work jobs active`);
    } catch (e) {
      toast.error('Cleanup failed. Please try again.');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <>
            <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="sw-cleanup-panel">
              <div>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" /> Social-Work-Required standard
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Re-checks every job against the strict rule and hides imported non-social-work listings (RN, physician, LPC/LMFT-only, etc.). Reversible — nothing is deleted.
                </p>
                {cleanupResult && (
                  <p className="text-sm text-emerald-700 mt-2" data-testid="sw-cleanup-result">
                    Scanned {cleanupResult.scanned} · Hidden {cleanupResult.archived} · Kept {cleanupResult.kept} · <strong>{cleanupResult.active_after} active social-work jobs</strong>
                  </p>
                )}
              </div>
              <Button onClick={runCleanup} disabled={cleaning} className="bg-primary hover:bg-primary-hover text-white shrink-0" data-testid="rerun-sw-cleanup-btn">
                {cleaning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cleaning…</> : <><Shield className="w-4 h-4 mr-2" /> Re-run Job Cleanup</>}
              </Button>
            </div>
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setOnlyMissingSalary((v) => !v)}
                className={`text-sm px-3 py-1.5 rounded-full border ${onlyMissingSalary ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300'}`}
                data-testid="missing-salary-filter"
              >
                {onlyMissingSalary ? 'Showing: Missing Salary' : `Missing Salary (${missingCount})`}
              </button>
              <span className="text-xs text-gray-500" data-testid="jobs-visible-count">{visibleJobs.length} jobs</span>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Company</th>
                      <th>License</th>
                      <th>Salary</th>
                      <th>Applications</th>
                      <th>Status</th>
                      <th>Posted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleJobs.map((job) => (
                      <tr key={job.job_id}>
                        <td className="font-medium">{job.title}</td>
                        <td>{job.company_name}</td>
                        <td>
                          <Badge className={getStatusBadgeClass(job.license_required === 'LMSW' ? 'info' : job.license_required === 'LCSW' ? 'active' : 'pending')}>
                            {job.license_required}
                          </Badge>
                        </td>
                        <td>
                          {(job.salary_min != null || job.salary_max != null)
                            ? <span className="text-gray-700">${job.salary_min ?? '?'}–${job.salary_max ?? '?'}</span>
                            : isMissingSalary(job)
                              ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Missing</Badge>
                              : <span className="text-gray-400">—</span>}
                        </td>
                        <td>{job.application_count || 0}</td>
                        <td>
                          <Badge className={getStatusBadgeClass(job.status)}>
                            {job.status}
                          </Badge>
                        </td>
                        <td>{formatDate(job.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
    </>
  );
};

export default AdminTabJobs;
